import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  ScanLine,
  Calendar,
  BarChart3,
  Activity,
  Users,
  FileText,
  Layers,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE;
const TYPHOON_OCR_BANNER =
  "https://opentyphoon.ai/images/models/Typhoon-OCR-KV.jpg?dpl=dpl_FGLHbxdvJUu14wCPwigKWtSQvSLu";
const TYPHOON_OCR_LINK = "https://opentyphoon.ai/";

export default function OcrUsageDashboard() {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [chartData, setChartData] = useState([]);
  const [monthDetail, setMonthDetail] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const [dailyRes, detailRes] = await Promise.all([
          authFetch(`${API_BASE}/admin/ocr-usage/daily?month=${month}`),
          authFetch(`${API_BASE}/admin/ocr-usage?period=month&date=${month}&limit=100`),
        ]);
        const dailyJson = dailyRes.ok ? await dailyRes.json() : { data: [] };
        const detailJson = detailRes.ok ? await detailRes.json() : null;
        if (isMounted) {
          setChartData(dailyJson.data || []);
          setMonthDetail(detailJson);
        }
      } catch (err) {
        console.error("Failed to load OCR usage dashboard:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [authFetch, month]);

  const fmt = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));

  const maxUsers = Math.max(...chartData.map((d) => d.active_users), 5);
  const totalUsersInMonth = chartData.reduce((sum, d) => sum + d.active_users, 0);
  const avgUsersPerDay =
    chartData.length > 0 ? (totalUsersInMonth / chartData.length).toFixed(1) : "0";

  const [yearStr, monthStr] = month.split("-");
  const daysInMonth = new Date(yearStr, monthStr, 0).getDate();
  const fullChartData = Array.from({ length: daysInMonth }, (_, i) => {
    const day = String(i + 1).padStart(2, "0");
    const dateStr = `${month}-${day}`;
    const existing = chartData.find((d) => d.date === dateStr);
    return (
      existing || {
        date: dateStr,
        active_users: 0,
        processes: 0,
        ocr_pages: 0,
        uploads: 0,
      }
    );
  });

  const summary = monthDetail?.summary || {};
  const byUser = monthDetail?.by_user || [];
  const recentRows = monthDetail?.rows || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-slate-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-950 via-purple-900 to-indigo-950 px-6 py-4 flex items-center gap-4 sticky top-0 z-20 shadow-lg shadow-purple-900/20">
        <button
          onClick={() => navigate("/")}
          className="p-2 rounded-xl hover:bg-white/10 text-violet-200 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <ScanLine className="text-fuchsia-300" size={20} />
            สถิติการใช้งาน Typhoon OCR
          </h1>
          <p className="text-xs text-violet-200/80 font-medium">
            ดูจำนวนผู้ใช้ OCR และการประมวลผลเอกสารรายวัน
          </p>
        </div>
      </header>

      <main className="flex-1 p-4 md:p-6 max-w-5xl w-full mx-auto space-y-6">
        {/* Typhoon OCR Banner */}
        <a
          href={TYPHOON_OCR_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="block group relative overflow-hidden rounded-2xl border border-violet-200/60 shadow-xl shadow-purple-900/10"
        >
          <img
            src={TYPHOON_OCR_BANNER}
            alt="Typhoon OCR"
            className="w-full h-36 sm:h-44 md:h-52 object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-violet-950/70 via-transparent to-transparent" />
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs font-semibold text-white/90 bg-violet-900/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20 group-hover:bg-violet-800/80 transition-colors">
            <ExternalLink size={12} />
            opentyphoon.ai
          </div>
        </a>

        {/* Controls & Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-violet-100 shadow-sm flex flex-col justify-center">
            <div className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Calendar size={14} className="text-violet-500" />
              เลือกเดือน
            </div>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full px-4 py-2.5 bg-violet-50/50 border border-violet-200 rounded-xl text-sm font-bold text-violet-900 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 outline-none transition-all"
            />
          </div>

          <div className="bg-gradient-to-br from-violet-700 via-purple-700 to-fuchsia-700 rounded-2xl p-5 border border-violet-500/30 shadow-lg shadow-purple-500/25 text-white flex flex-col justify-center relative overflow-hidden">
            <div className="absolute right-[-10%] top-[-20%] w-24 h-24 bg-white/10 rounded-full blur-xl" />
            <div className="text-xs font-medium text-violet-100 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Users size={14} />
              รวมผู้ใช้ OCR (สะสม)
            </div>
            <div className="text-3xl font-extrabold">
              {fmt(summary.active_users ?? totalUsersInMonth)}{" "}
              <span className="text-sm font-medium text-violet-200">คน</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-violet-100 shadow-sm flex flex-col justify-center">
            <div className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Activity size={14} className="text-fuchsia-500" />
              เฉลี่ยต่อวัน
            </div>
            <div className="text-3xl font-extrabold text-violet-950">
              {avgUsersPerDay}{" "}
              <span className="text-sm font-medium text-violet-400">คน/วัน</span>
            </div>
          </div>
        </div>

        {/* Month stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {
              label: "ประมวลผลสำเร็จ",
              value: summary.process_success,
              sub: `จาก ${fmt(summary.processes)} ครั้ง`,
              tint: "from-violet-500/10 to-purple-500/10 border-violet-200",
            },
            {
              label: "หน้า OCR",
              value: summary.ocr_pages,
              sub: `รวม ${fmt(summary.total_pages)} หน้า`,
              tint: "from-fuchsia-500/10 to-pink-500/10 border-fuchsia-200",
            },
            {
              label: "อัปโหลดไฟล์",
              value: summary.uploads,
              sub: "ไฟล์ที่อัปโหลด",
              tint: "from-indigo-500/10 to-violet-500/10 border-indigo-200",
            },
            {
              label: "เหตุการณ์ทั้งหมด",
              value: summary.total_events,
              sub: "upload + process",
              tint: "from-purple-500/10 to-violet-500/10 border-purple-200",
            },
          ].map((item) => (
            <div
              key={item.label}
              className={`rounded-xl bg-gradient-to-br ${item.tint} border p-4`}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-violet-600 mb-1">
                {item.label}
              </div>
              <div className="text-xl font-bold text-violet-950">{fmt(item.value)}</div>
              <div className="text-[10px] text-violet-400 mt-1">{item.sub}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-violet-100 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-base font-bold text-violet-950 flex items-center gap-2">
              <BarChart3 size={18} className="text-violet-600" />
              กราฟผู้ใช้ OCR รายวัน
            </h3>
            <div className="text-xs font-medium text-violet-600 bg-violet-50 px-3 py-1 rounded-full border border-violet-100">
              เดือน {month}
            </div>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="relative h-72 w-full mt-4">
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-full border-t border-violet-50 border-dashed flex-1 flex items-start"
                  >
                    <span className="text-[10px] text-violet-300 -mt-2 -ml-6 w-5 text-right">
                      {Math.round(maxUsers - (maxUsers / 4) * i)}
                    </span>
                  </div>
                ))}
                <div className="w-full border-t border-violet-200">
                  <span className="text-[10px] text-violet-300 -mt-2 -ml-6 w-5 text-right block">
                    0
                  </span>
                </div>
              </div>

              <div className="absolute inset-0 ml-2 flex items-end justify-between gap-1 sm:gap-2 pb-[1px]">
                {fullChartData.map((d, i) => {
                  const heightPercent = (d.active_users / maxUsers) * 100;
                  const dayNum = d.date.split("-")[2];
                  const isWeekend =
                    new Date(d.date).getDay() === 0 || new Date(d.date).getDay() === 6;

                  return (
                    <div
                      key={i}
                      className="relative flex-1 flex flex-col items-center justify-end h-full group"
                    >
                      <div className="absolute -top-14 opacity-0 group-hover:opacity-100 transition-opacity bg-violet-950 text-white text-[10px] py-1.5 px-2 rounded-lg whitespace-nowrap z-10 pointer-events-none shadow-lg">
                        วันที่ {dayNum}:{" "}
                        <span className="font-bold">{d.active_users} คน</span>
                        {d.processes > 0 && (
                          <span className="text-violet-200">
                            {" "}
                            · {d.processes} ครั้ง · {d.ocr_pages} หน้า
                          </span>
                        )}
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-violet-950 rotate-45" />
                      </div>

                      <div
                        className={`w-full max-w-[24px] rounded-t-sm transition-all duration-500 ease-out group-hover:opacity-80 cursor-pointer
                          ${
                            d.active_users > 0
                              ? "bg-gradient-to-t from-violet-700 via-purple-600 to-fuchsia-400 shadow-[0_0_12px_rgba(139,92,246,0.35)]"
                              : "bg-transparent"
                          }`}
                        style={{
                          height: `${Math.max(heightPercent, 0)}%`,
                          minHeight: d.active_users > 0 ? "4px" : "0",
                        }}
                      />

                      <div
                        className={`absolute -bottom-6 text-[9px] font-medium ${
                          isWeekend ? "text-fuchsia-400" : "text-violet-400"
                        } ${i % 2 !== 0 ? "hidden sm:block" : ""}`}
                      >
                        {dayNum}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* By user & recent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-violet-100 shadow-sm">
            <h3 className="text-sm font-bold text-violet-950 mb-4 flex items-center gap-2">
              <Layers size={16} className="text-violet-600" />
              สรุปตามผู้ใช้
            </h3>
            {byUser.length === 0 ? (
              <div className="text-center text-violet-300 text-xs py-8">
                ไม่มีข้อมูล OCR ในเดือนนี้
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {byUser.map((row) => (
                  <div
                    key={row.user_id}
                    className="flex items-center justify-between gap-3 rounded-xl bg-violet-50/60 border border-violet-100 px-3 py-2.5 text-xs"
                  >
                    <span className="font-semibold text-violet-900 truncate">
                      {row.user_id}
                    </span>
                    <span className="text-violet-500 shrink-0">
                      {fmt(row.processes)} ครั้ง · {fmt(row.ocr_pages)} หน้า OCR
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-5 border border-violet-100 shadow-sm">
            <h3 className="text-sm font-bold text-violet-950 mb-4 flex items-center gap-2">
              <FileText size={16} className="text-violet-600" />
              รายการล่าสุด
            </h3>
            {recentRows.length === 0 ? (
              <div className="text-center text-violet-300 text-xs py-8">ยังไม่มีรายการ</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {recentRows.slice(0, 15).map((row) => (
                  <div
                    key={row.id}
                    className="rounded-xl border border-violet-100 bg-violet-50/40 px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-semibold text-violet-900 truncate">
                        {row.user_id}
                      </span>
                      <span
                        className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          row.status === "success"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {row.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-violet-600 mt-1 truncate">
                      {row.action === "process" ? "ประมวลผล" : "อัปโหลด"}
                      {row.file_name ? ` · ${row.file_name}` : ""}
                    </div>
                    {row.action === "process" && (
                      <div className="text-[10px] text-violet-400 mt-1">
                        {fmt(row.ocr_pages)} หน้า OCR / {fmt(row.total_pages)} หน้า
                        {row.ocr_model ? ` · ${row.ocr_model}` : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
